// src/components/Admin/AssignedProjectsList.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import { AgGridReact } from 'ag-grid-react';
import {
  ClientSideRowModelModule,
  ClipboardModule,
  RichSelectModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
  CheckboxEditorModule,
  RenderApiModule,
  ModuleRegistry,
} from 'ag-grid-enterprise';

// Register AG Grid Enterprise modules
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  RichSelectModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
  CheckboxEditorModule,
  RenderApiModule,
]);

// Stub for useFetchProjects. Replace with your real hook if you have one.
function useFetchProjects() {
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const { data } = await axios.get('/api/Project/get-all-projects');
        setProjects(data.map((p) => ({ id: p.projectId, name: p.name })));
      } catch (err) {
        console.error('Failed to fetch projects', err);
      }
    };
    fetchAll();
  }, []);
  return projects;
}

export default function AssignedProjectsList() {
  const gridRef = useRef(null);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    id: '',
    name: '',
    userName: '',
    userGroupId: '',
    userGroupName: '',
    assignedProjects: [], // array of { id, name }
  });

  // Which project is currently selected in the “add” dropdown:
  const [projectToAdd, setProjectToAdd] = useState(null);

  // Fetch all projects once
  const allProjects = useFetchProjects();

  // 1) On mount and after any save, fetch “users with assigned projects”:
  const fetchNestedUsers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/Admin/users-assigned-nested-permissions');
      
        const usersWithAssignedProjects = data.filter(
            (u) => u.userGroupProjectAccessDependant == true);
      const rows = usersWithAssignedProjects.map((u) => ({
        id: u.id,
        name: u.name,
        userName: u.userName,
        userGroupId: u.userGroupId ?? '',
        userGroupName: u.userGroupName ?? '',
        assignedProjects: u.assignedProjects.map((p) => ({ id: p.projectId, name: p.name })),
      }));
      setRowData(rows);
    } catch (err) {
      console.error('Failed to fetch users with assigned projects', err);
      setRowData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNestedUsers();
  }, []);

  // 2) Column definitions, including “Assigned Projects”:
  const columnDefs = useMemo(
    () => [
        { headerName: 'ID', field: 'id', hide: true },
        {
            headerName: 'Actions',
            field: 'actions',
            minWidth: 100,
            cellRenderer: (params) => (
            <IconButton size="small" onClick={() => handleEditClick(params.data)}>
                <EditIcon fontSize="small" />
            </IconButton>
            ),
        },
        { headerName: 'Name', field: 'name', flex: 1, sort: 'asc' },
        { headerName: 'Username', field: 'userName', flex: 1 },
        { headerName: 'GroupId', field: 'userGroupId', hide: true },
        { headerName: 'Group', field: 'userGroupName', flex: 1 },
        {
        headerName: 'Assigned Projects',
        field: 'assignedProjects',
        flex: 2,
        cellStyle: { textAlign: 'left', whiteSpace: 'normal' },
        autoHeight: true,
        wrapText: true,
        // ① Add a valueFormatter so AG Grid can see a string when it needs one:
        valueFormatter: (params) => {
            const list = params.value || [];
            return list.map((p) => p.name).join(', ');
        },
        // ② Keep your existing cellRenderer for the multi-line display:
        cellRenderer: (params) => {
            const list = params.value || [];
            if (list.length === 0) {
            return (
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#888' }}>
                None
                </Typography>
            );
            }
            return (
            <Box>
                {list.map((p) => (
                <Typography key={p.id} variant="body2">
                    {p.name}
                </Typography>
                ))}
            </Box>
            );
        },
        },
    ],
    []
  );

  // 3) When “Edit” is clicked, open dialog with that user’s data:
  const handleEditClick = (row) => {
    setIsEditMode(true);
    setCurrentUser({
      id: row.id,
      name: row.name,
      userName: row.userName,
      userGroupId: row.userGroupId,
      userGroupName: row.userGroupName,
      assignedProjects: row.assignedProjects.slice(), // already present in rowData
    });
    setProjectToAdd(null);
    setDialogOpen(true);
  };

  // 4a) Locally add a project to currentUser.assignedProjects
  const handleAddProjectClick = () => {
    if (!projectToAdd) return;
    // Prevent duplicates:
    const exists = currentUser.assignedProjects.some((p) => p.id === projectToAdd.id);
    if (!exists) {
      setCurrentUser((prev) => ({
        ...prev,
        assignedProjects: [...prev.assignedProjects, projectToAdd],
      }));
    }
    setProjectToAdd(null);
  };

  // 4b) Locally remove a project from currentUser.assignedProjects
  const handleRemoveProject = (projectId) => {
    setCurrentUser((prev) => ({
      ...prev,
      assignedProjects: prev.assignedProjects.filter((p) => p.id !== projectId),
    }));
  };

  // 5) Save assigned projects for this user
  const handleSaveAssignments = async () => {
    const payload = {
        userId: currentUser.id,
        projectIds: currentUser.assignedProjects.map((p) => p.id),
    };

    try {
      await axios.put(
        `/api/Admin/user/assigned-projects`,
        payload
      );
      // Refresh the table by re-fetching from /users-assigned-projects
      await fetchNestedUsers();
      setDialogOpen(false);
    } catch (err) {
      console.error('Failed to save assigned projects', err);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          mb: 2,
        }}
      >
    </Box>

      <div style={{ height: '74vh', width: '100%' }}>
        <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            rowSelection={{
                mode: 'multiRow',
                checkboxes: false,
                headerCheckbox: false,
                suppressRowClickSelection: true,
            }}
            animateRows
            loading={loading}
            defaultColDef={{ resizable: true }}
            domLayout="autoHeight"
        />
      </div>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditMode
            ? `Edit Projects for ${currentUser.name}`
            : 'Assign Projects'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* 4a) Dropdown to pick one project at a time */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Autocomplete
                sx={{ flexGrow: 1 }}
                options={allProjects}
                getOptionLabel={(opt) => opt.name}
                value={projectToAdd}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                onChange={(e, newVal) => setProjectToAdd(newVal)}
                renderInput={(params) => <TextField {...params} label="Pick a project to add" />}
              />
              <Button
                variant="contained"
                onClick={handleAddProjectClick}
                disabled={!projectToAdd}
              >
                Add
              </Button>
            </Box>

            {/* 4b) Show list of already‐assigned projects, each with a delete icon */}
            <List dense>
              {currentUser.assignedProjects.length === 0 && (
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#888', p: 1 }}>
                  No projects assigned.
                </Typography>
              )}
              {currentUser.assignedProjects.map((proj) => (
                <ListItem key={proj.id} sx={{ py: 0, px: 1 }}>
                  <ListItemText primary={proj.name} />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleRemoveProject(proj.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAssignments}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
